<template>
  <div v-if="stats" class="candidates-dashboard-stats mb-4">
    <div
      v-for="tile in tiles"
      :key="tile.key"
      class="candidates-dashboard-stats__tile"
    >
      <span class="candidates-dashboard-stats__icon">
        <b-icon :icon="tile.icon" />
      </span>
      <div class="candidates-dashboard-stats__text">
        <span class="candidates-dashboard-stats__value">{{ tile.value }}</span>
        <span class="candidates-dashboard-stats__label">{{ tile.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

// Company-side OTW dashboard (build spec §3.2 Week 5 — "Company dashboard
// OTW additions section"). Fields match GET /open-to-work/dashboard exactly
// as documented in the eng spec (candidates_available,
// new_candidates_this_week, resume_requests_sent/accepted, saved_candidates,
// workforce_invitations_sent/accepted, tokens_used_this_month,
// workers_added_this_month) — only the presentation changed here.
export default {
  name: 'CandidatesDashboardStats',

  computed: {
    ...mapGetters('candidate', ['dashboardStats']),
    ...mapGetters('auth', ['user']),

    stats() {
      return this.dashboardStats
    },

    // Free tier / trial — the token economy doesn't apply to them, so the
    // "Tokens Used This Month" tile is always 0 and just noise.
    isFree() {
      return !!(this.user && this.user.is_free)
    },

    tiles() {
      const s = this.stats || {}
      return [
        {
          key: 'available',
          label: 'Available Candidates',
          value: s.candidates_available || 0,
          icon: 'people-fill',
        },
        {
          key: 'new',
          label: 'New This Week',
          value: s.new_candidates_this_week || 0,
          icon: 'stars',
        },
        {
          key: 'saved',
          label: 'Saved Candidates',
          value: s.saved_candidates || 0,
          icon: 'bookmark-star-fill',
        },
        {
          key: 'resumeSent',
          label: 'Resume Requests Sent',
          value: s.resume_requests_sent || 0,
          icon: 'file-earmark-arrow-up-fill',
        },
        {
          key: 'resumeAccepted',
          label: 'Resume Requests Accepted',
          value: s.resume_requests_accepted || 0,
          icon: 'file-earmark-check-fill',
        },
        {
          key: 'invitesSent',
          label: 'Workforce Invitations Sent',
          value: s.workforce_invitations_sent || 0,
          icon: 'envelope-fill',
        },
        {
          key: 'invitesAccepted',
          label: 'Workforce Invitations Accepted',
          value: s.workforce_invitations_accepted || 0,
          icon: 'person-check-fill',
        },
        {
          key: 'workersAdded',
          label: 'Workers Added This Month',
          value: s.workers_added_this_month || 0,
          icon: 'person-plus-fill',
        },
        ...(this.isFree
          ? []
          : [
              {
                key: 'tokensUsed',
                label: 'Tokens Used This Month',
                value: s.tokens_used_this_month || 0,
                icon: 'credit-card-fill',
              },
            ]),
      ]
    },
  },

  created() {
    this.fetchDashboardStats().catch(() => {})
  },

  methods: {
    ...mapActions('candidate', ['fetchDashboardStats']),
  },
}
</script>

<style lang="scss" scoped>
.candidates-dashboard-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

@media (max-width: 991.98px) {
  .candidates-dashboard-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 575.98px) {
  .candidates-dashboard-stats {
    grid-template-columns: 1fr;
  }
}

.candidates-dashboard-stats__tile {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1.1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid #e1e6ea;
  background-color: #fff;
}

.candidates-dashboard-stats__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background-color: #e6f0ff;
  color: #006cff;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.candidates-dashboard-stats__text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.candidates-dashboard-stats__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #212529;
  line-height: 1.2;
}

.candidates-dashboard-stats__label {
  font-size: 0.78rem;
  color: #6c7581;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
